import {
  CHAT_INBOX_EXPIRY_DATE,
  CHAT_INBOX_RELEASE_DATE,
  FEATURE_FLAG_EXPIRY_DATE,
  USER_CREATION_CUTOFF_DATE
} from "./constants";
import {
  PromoCloseReason,
  PromoFeatureName,
  PromoShouldApplyData,
  PromoType
} from "./types";
import moment from "moment";
import { track } from "shared/telemetry/track";
import { writeLocalStorage } from "shared/hooks/use-local-storage";

const checkCombinedIncomePromo = ({
  isBoomScreen,
  partnerCreatedAt,
  featureFlagEnabled,
  sessionPromoData
}: PromoShouldApplyData) => {
  const now = moment();
  const shouldHide = !!Number(sessionPromoData);

  if (
    !isBoomScreen ||
    now.isAfter(FEATURE_FLAG_EXPIRY_DATE) ||
    (partnerCreatedAt
      ? moment(partnerCreatedAt).isAfter(USER_CREATION_CUTOFF_DATE)
      : false)
  )
    return false;

  return Boolean(featureFlagEnabled && !shouldHide);
};

// Dismissal is permanent and survives sessions, so it lives in localStorage
// rather than the session-scoped storage the older promos use.
export const chatInboxPromoStorageKey = (kind: "modalSeen" | "dismissed") =>
  `${PromoFeatureName.ChatMovedToInbox}_promo_${kind}`;

const checkChatMovedToInboxPromo = ({
  isChatInboxAudience,
  partnerCreatedAt,
  dismissed
}: PromoShouldApplyData) => {
  const joinedAfterTheMove = partnerCreatedAt
    ? moment(partnerCreatedAt).isAfter(CHAT_INBOX_RELEASE_DATE)
    : true;

  return Boolean(
    isChatInboxAudience &&
    !dismissed &&
    !joinedAfterTheMove &&
    !moment().isAfter(CHAT_INBOX_EXPIRY_DATE)
  );
};

// The modal shows once; closing it steps the announcement down to the banner.
const checkChatMovedToInboxModal = (data: PromoShouldApplyData) =>
  checkChatMovedToInboxPromo(data) && !data.modalSeen;

const checkChatMovedToInboxBanner = (data: PromoShouldApplyData) =>
  checkChatMovedToInboxPromo(data) && Boolean(data.modalSeen);

const RULES_PER_FEATURE = {
  [PromoFeatureName.CombinedIncome]: {
    [PromoType.Modal]: {
      shouldApply: checkCombinedIncomePromo
    },
    [PromoType.Banner]: {
      shouldApply: checkCombinedIncomePromo
    }
  },
  [PromoFeatureName.ChatMovedToInbox]: {
    [PromoType.Modal]: {
      shouldApply: checkChatMovedToInboxModal
    },
    [PromoType.Banner]: {
      shouldApply: checkChatMovedToInboxBanner
    }
  }
};

export const shouldApplyPromo = (
  featureName: PromoFeatureName,
  type: PromoType,
  context: PromoShouldApplyData
) => RULES_PER_FEATURE[featureName][type].shouldApply(context);

export const cleanupPromoStorage = (featureName: PromoFeatureName) => {
  sessionStorage.removeItem("hidePromoModal");
  sessionStorage.removeItem("hidePromoBanner");
  localStorage.removeItem(`${featureName}_promoBannerSeenCount`);
};

export const cleanupChatInboxPromoStorage = () => {
  localStorage.removeItem(chatInboxPromoStorageKey("modalSeen"));
  localStorage.removeItem(chatInboxPromoStorageKey("dismissed"));
};

const markChatInboxPromoAsSeen = (
  type: PromoType,
  reason: PromoCloseReason
) => {
  // Only closing the modal keeps the announcement alive (as the banner); the
  // action button and the banner itself retire it for good.
  const dismissForGood = reason === "action" || type === PromoType.Banner;

  if (dismissForGood)
    writeLocalStorage(chatInboxPromoStorageKey("dismissed"), "1");

  writeLocalStorage(chatInboxPromoStorageKey("modalSeen"), "1");

  track(`promo.${PromoFeatureName.ChatMovedToInbox}.${type}.close`, {
    dismissed: dismissForGood
  });
};

export const markPromoAsSeen = (
  featureName: PromoFeatureName,
  type: PromoType,
  reason: PromoCloseReason = "close"
) => {
  if (featureName === PromoFeatureName.ChatMovedToInbox) {
    markChatInboxPromoAsSeen(type, reason);
    return;
  }

  const promoBannerSeenCount = localStorage.getItem(
    `${featureName}_promoBannerSeenCount`
  );
  let featureFlagPersonProperties:
    | { seen_count: number }
    | { seen_modal: boolean };

  if (type === PromoType.Modal) {
    sessionStorage.setItem("hidePromoModal", "1");
    featureFlagPersonProperties = {
      seen_modal: true
    };
  } else {
    const seenBannerCount = Number(promoBannerSeenCount) + 1;

    localStorage.setItem(
      `${featureName}_promoBannerSeenCount`,
      `${seenBannerCount}`
    );
    featureFlagPersonProperties = {
      seen_count: seenBannerCount
    };
  }

  sessionStorage.setItem("hidePromoBanner", "1");
  track(`promo.${featureName}.${type}.close`, featureFlagPersonProperties);
};

const ACTION_EVENT_PER_FEATURE = {
  [PromoFeatureName.CombinedIncome]: "promo.combined_income.watch_demo",
  [PromoFeatureName.ChatMovedToInbox]: "promo.chat_moved_to_inbox.action_click"
} as const;

export const trackPromoRUM = (
  featureName: PromoFeatureName,
  type: PromoType,
  actionType: "show" | "action-click" | "close-click"
) => {
  if (actionType === "show") track(`promo.${featureName}.show`, { type });
  if (actionType === "action-click")
    track(ACTION_EVENT_PER_FEATURE[featureName], { type });
};
